// resources/js/Pages/Formula/FormulaEdit.js
// DND: FINAL FIX - Using createPortal for the DragOverlay to solve positioning and collision issues.

import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom'; // DND: Import createPortal
import { Head, router, useForm, usePage } from '@inertiajs/react';
import { v4 as uuid } from 'uuid';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Row, Col, Form, Button, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import LocalTinyMCE from '../../Components/LocalTinyMCE';
import SchemaBuilder from './SchemaBuilder';
import QuestionManager from './questions/QuestionManager';
import VariablesSidebar from './questions/VariablesSidebar';
import SimpleModeSidebar from './questions/SimpleModeSidebar'; // <-- Import the new sidebar
import { useVariableInsertion } from './questions/questionHooks';
import { validatePromptVariables } from './questions/questionUtils';
import { useSchemaImportExport } from './questions/SchemaImportExport';
import SchemaImportModal from './questions/SchemaImportModal';
import WYSIWYGCanvas from './WYSIWYGCanvas';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay, // DND: Import DragOverlay
  // DND: Import more advanced collision detection strategies
  pointerWithin,
  rectIntersection,
  getFirstCollision,
} from '@dnd-kit/core';
import { relocateNode, moveNodeToPosition } from './utils/schemaUpdates';

// Helper to find a node in the schema tree
const findNodeById = (nodes, id) => {
  if (!nodes) return null;
  if (nodes.id === id) return nodes;
  if (nodes.children) {
    for (const child of nodes.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
};

// In resources/js/Pages/Formula/FormulaEdit.js, before the main export
function CompactTryFormula({ formula, recentScripts }) {
  const [selectedScript, setSelectedScript] = useState(recentScripts?.my_scripts?.[0]?.hash || '');

  const handleTryFormula = () => {
    if (selectedScript && formula.unique_name) {
      const url = `/formula/use_formula?id=${formula.unique_name}&movieid=${selectedScript}&test_mode=1`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Select
        size="sm"
        value={selectedScript}
        onChange={(e) => setSelectedScript(e.target.value)}
        style={{ minWidth: '200px' }}
      >
        <option value="" disabled>Select a script...</option>
        {recentScripts.my_scripts?.length > 0 && (
          <optgroup label="My Scripts">
            {recentScripts.my_scripts.map(script => (
              <option key={script.hash} value={script.hash}>
                {script.title}
              </option>
            ))}
          </optgroup>
        )}
        {recentScripts.script_vault?.length > 0 && (
          <optgroup label="Script Vault">
            {recentScripts.script_vault.map(script => (
              <option key={script.hash} value={script.hash}>
                {script.title}
              </option>
            ))}
          </optgroup>
        )}
      </Form.Select>
      <Button
        variant="outline-primary"
        size="sm"
        disabled={!selectedScript}
        onClick={handleTryFormula}
        className="flex-shrink-0"
      >
        🎬 Try Formula
      </Button>
    </div>
  );
}

export default function FormulaEdit({ formula, variables, recentScripts = {}, isAdmin, errors = {}, flash = {}, allResources = [] }) {
  const promptTextareaRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSchemaImport, setShowSchemaImport] = useState(false);
  const [localPromptText, setLocalPromptText] = useState('');
  const [localLongDescription, setLocalLongDescription] = useState('');
  const [localAboutAuthor, setLocalAboutAuthor] = useState('');
  const [localResultDescription, setLocalResultDescription] = useState('');
  const [localVideoOverviewUrl, setLocalVideoOverviewUrl] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [linkedResources, setLinkedResources] = useState([]);
  const linkedResourcesRef = useRef([]);
  const [resourceSearch, setResourceSearch] = useState('');
  const [clientErrors, setClientErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('questions'); // Default to the main tab

  const { exportSchemaToFile } = useSchemaImportExport();
  const updateTimeoutRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(updateTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Keep ref AND form.data in sync so save() always has the latest value
  useEffect(() => {
    linkedResourcesRef.current = linkedResources;
    // Also sync into Inertia form data so it's included in the PUT payload
    form.setData('resources', linkedResources.map((r, i) => ({
      id: r.id,
      sort_order: i,
      context_note: r.context_note || null,
      is_enabled: r.is_enabled !== false,
    })));
  }, [linkedResources]);

  const DebugVariables = ({ questions }) => {
    const [showDebug, setShowDebug] = useState(false);
    if (process.env.NODE_ENV !== 'development') {
      return null;
    }

    return (
      <div className="border rounded p-2 mb-3 bg-warning bg-opacity-10">
        <button
          className="btn btn-sm btn-outline-warning"
          onClick={() => setShowDebug(!showDebug)}
        >
          🐛 Debug Variables ({questions?.length || 0})
        </button>

        {showDebug && (
          <div className="mt-2">
            <small>
              <strong>Questions:</strong>
              <pre className="mt-1 small bg-light p-2 rounded">
                {JSON.stringify(questions?.map(q => ({
                  id: q.id,
                  variable_name: q.variable_name,
                  label: q.label,
                  type: q.type
                })), null, 2)}
              </pre>
            </small>
          </div>
        )}
      </div>
    );
  };

  if (!formula) {
    console.error('Formula is undefined! Props:', { formula, variables });
    return <div>Error: Formula not found</div>;
  }

  const parsedContextSettings = useMemo(() => {
    const parseContextSettings = (contextData) => {
      if (!contextData) return { context_inclusion: 'synopsis', include_character_descriptions: false };
      if (typeof contextData === 'object') return contextData;
      if (typeof contextData === 'string') {
        try {
          return JSON.parse(contextData);
        } catch (e) {
          console.warn('Failed to parse context settings:', e);
          return { context_inclusion: 'synopsis', include_character_descriptions: false };
        }
      }
      return { context_inclusion: 'synopsis', include_character_descriptions: false };
    };
    return parseContextSettings(formula.context_settings);
  }, [formula.context_settings]);

  const parsedQuestions = useMemo(() => {
    const parseQuestions = (fieldsData) => {
      if (!fieldsData) return [];
      if (Array.isArray(fieldsData)) return fieldsData;
      if (typeof fieldsData === 'string') {
        try {
          const parsed = JSON.parse(fieldsData);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return [];
    };
    return parseQuestions(formula.fields);
  }, [formula.fields]);

  const parsedSchema = useMemo(() => {
    const parseSchema = (schemaData) => {
      if (!schemaData) return null;
      if (typeof schemaData === 'object' && schemaData.id) return schemaData;
      if (typeof schemaData === 'string') {
        try {
          const parsed = JSON.parse(schemaData);
          return (parsed && parsed.id) ? parsed : null;
        } catch (e) {
          return null;
        }
      }
      return null;
    };
    return parseSchema(formula.output_fields);
  }, [formula.output_fields]);

  const form = useForm({
    listed: formula.list || false,
    title: formula.title || '',
    description: formula.description || '',
    long_description: formula.long_description || '',
    about_author: formula.about_author || '',
    result_description: formula.result_description || '',
    video_overview_url: formula.video_overview_url || '',
    explanation_view: formula.explanation_view || '',
    prompt_json: ensureRoot(parsedSchema),
    questions: parsedQuestions,
    prompt_text: formula.prompt || '',
    prompt_mode: isAdmin ? (formula.prompt_mode || 'advanced') : 'simple', // <-- SECURITY
    context_inclusion: parsedContextSettings.context_inclusion || 'synopsis',
    include_character_descriptions: parsedContextSettings.include_character_descriptions || false,
    resources: [],
  });

  useEffect(() => {
    setLocalPromptText(form.data.prompt_text || '');
    setLocalLongDescription(form.data.long_description || '');
    setLocalAboutAuthor(form.data.about_author || '');
    setLocalResultDescription(form.data.result_description || '');
    setLocalVideoOverviewUrl(form.data.video_overview_url || '');
    setLocalDescription(form.data.description || '');
    // Initialize linked resources from formula's pivot data
    if (formula.guide_resources && formula.guide_resources.length > 0) {
      setLinkedResources(formula.guide_resources.map((r, i) => ({
        id: r.id,
        slug: r.slug,
        title: r.title,
        sort_order: r.pivot?.sort_order ?? i,
        context_note: r.pivot?.context_note || '',
        is_enabled: r.pivot?.is_enabled ?? true,
      })));
    }
  }, []);

  useEffect(() => {
    if (document.activeElement !== promptTextareaRef.current) {
      setLocalPromptText(form.data.prompt_text || '');
    }
  }, [form.data.prompt_text]);

  const debouncedFormUpdate = useCallback((field, value) => {
    if (!form || !form.setData) return;
    if (updateTimeoutRef.current[field]) {
      clearTimeout(updateTimeoutRef.current[field]);
    }
    updateTimeoutRef.current[field] = setTimeout(() => {
      form.setData(field, value);
    }, 500);
  }, [form]);

  const immediateFormUpdate = useCallback((field, value) => {
    if (updateTimeoutRef.current[field]) {
      clearTimeout(updateTimeoutRef.current[field]);
    }
    form.setData(field, value);
  }, [form]);

  const hasErrors = (errors && Object.keys(errors).length > 0) || (form.errors && Object.keys(form.errors).length > 0);

  useEffect(() => {
    if (flash?.success) toast.success(flash.success);
    if (flash?.error) toast.error(flash.error);
    if (flash?.warning) toast.warning(flash.warning);

    if (errors && Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, messages]) => {
        const messageArray = Array.isArray(messages) ? messages : [messages];
        toast.error(`${field}: ${messageArray.join(', ')}`);
      });
    }

    if (form && form.errors && Object.keys(form.errors).length > 0) {
      Object.entries(form.errors).forEach(([field, messages]) => {
        const messageArray = Array.isArray(messages) ? messages : [messages];
        toast.error(`${field}: ${messageArray.join(', ')}`);
      });
    }
  }, [flash, errors, form.errors]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.data.title?.trim()) errors.title = 'Title is required';
    if (!localDescription?.trim()) errors.description = 'Description is required';
    // if (!form.data.questions?.length) errors.questions = 'At least one question is required';
    if (!localPromptText?.trim()) {
      errors.prompt_text = 'Prompt text is required';
    } else {
      const variableValidation = validatePromptVariables(localPromptText, form.data.questions);
      if (!variableValidation.isValid) {
        errors.prompt_text = variableValidation.message;
      }
    }
    if (!form.data.prompt_json?.children?.length) errors.prompt_json = 'Response schema must have at least one field';
    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form.data.title, localDescription, form.data.questions, localPromptText, form.data.prompt_json]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateForm();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [validateForm]);

  function ensureRoot(schema) {
    return schema && schema.id && schema.type === 'list' ? schema : { id: uuid(), type: 'list', key: 'Root', display: 'ul', children: [] };
  }

  const { insertVariable } = useVariableInsertion(promptTextareaRef, localPromptText, (newText) => {
    setLocalPromptText(newText);
    form.setData('prompt_text', newText);
  });

  const handleSchemaImport = useCallback((importedSchema) => {
    form.setData('prompt_json', importedSchema);
    toast.success('Schema imported successfully!');
  }, [form.setData]);

  const handleSchemaExport = useCallback(() => {
    exportSchemaToFile(form.data.prompt_json, form.data.title || 'formula');
  }, [exportSchemaToFile, form.data.prompt_json, form.data.title]);

  const handleQuestionsChange = useCallback((questions) => {
    form.setData('questions', questions);
  }, [form.setData]);

  // Schema changes sync immediately (not debounced) to prevent data loss when switching tabs
  const handleSchemaChange = useCallback((schema) => {
    form.setData('prompt_json', schema);
  }, [form.setData]);

  const handleContextChange = useCallback((key, value) => {
    form.setData(key, value);
  }, [form.setData]);

  // DND: State to track the active dragged item
  const [activeId, setActiveId] = useState(null);
  const [pointerCoordinates, setPointerCoordinates] = useState(null);

  // DND: Setup sensors for dnd-kit - Fixed sensor configuration with activation constraint
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,  // 150ms hold to start drag
        tolerance: 5,  // Allow 5px movement
      }
    }),
    useSensor(KeyboardSensor)
  );

  // DND: Enhanced collision detection strategy with insertion point priority
  const collisionDetectionStrategy = useCallback(
    (args) => {
      const pointerCollisions = pointerWithin(args);

      if (pointerCollisions.length > 0) {
        // Find if there's a container collision
        const containerCollision = pointerCollisions.find(collision => {
          const droppableData = collision.data?.current;
          return droppableData?.type === 'container';
        });

        // Find if there's an insertion point collision
        const insertionPointCollision = pointerCollisions.find(collision => {
          const droppableData = collision.data?.current;
          return droppableData?.type === 'insertion-point';
        });

        // If the pointer is over both a container and an insertion point...
        if (containerCollision && insertionPointCollision) {
          const containerData = containerCollision.data?.current;
          const containerNode = containerData?.node;

          // **THE FIX**: Prioritize dropping into an EMPTY container over its insertion point.
          // This allows the container itself to be the target.
          if (containerData?.isEmpty || (containerNode && (!containerNode.children || containerNode.children.length === 0))) {
            return [containerCollision];
          }

          // If the container is not empty, prioritize the insertion point for precise reordering.
          return [insertionPointCollision];
        }

        // If only one type is found, return that.
        if (insertionPointCollision) return [insertionPointCollision];
        if (containerCollision) return [containerCollision];
      }

      // Fallback to the original list or rectangle intersection if needed
      return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
    },
    []
  );

  // DND: Define drag event handlers for DragOverlay
  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setPointerCoordinates(event.activatorEvent ? {
      x: event.activatorEvent.clientX,
      y: event.activatorEvent.clientY
    } : null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over, delta } = event;
    setActiveId(null); // Clear active item
    setPointerCoordinates(null); // Clear pointer coordinates

    // If drag distance small, cancel
    if (Math.abs(delta.x) < 5 && Math.abs(delta.y) < 5) return;

    if (!over) return; // No drop target

    if (over && active.id !== over.id) {
      // Check if dropping on an insertion point
      if (over.id && over.id.toString().startsWith('insertion-point-')) {
        const insertionData = over.data?.current;
        if (insertionData && insertionData.type === 'insertion-point') {
          const { parentId, position } = insertionData;

          // Move the active item to the specified position within the parent
          const newSchema = moveNodeToPosition(form.data.prompt_json, {
            activeId: active.id,
            parentId: parentId,
            position: position
          });

          if (newSchema) {
            form.setData('prompt_json', newSchema);
          }
          return;
        }
      }

      // ✅ NEW: Handle dropping directly onto containers (especially empty ones)
      const overData = over.data?.current;
      if (overData?.type === 'container') {
        // If it's an empty container, add to position 0
        if (overData.isEmpty || (overData.node && (!overData.node.children || overData.node.children.length === 0))) {
          const newSchema = moveNodeToPosition(form.data.prompt_json, {
            activeId: active.id,
            parentId: over.id,
            position: 0
          });

          if (newSchema) {
            form.setData('prompt_json', newSchema);
          }
          return;
        }
      }

      // Fallback: Original drag handling for non-insertion point drops
      const overNodeRect = over.rect;
      const draggingNodeRect = active.rect.current.translated;
      const isBelow = draggingNodeRect && overNodeRect ?
        draggingNodeRect.top > overNodeRect.top + overNodeRect.height / 2 : false;

      const newSchema = relocateNode(form.data.prompt_json, {
        activeId: active.id,
        overId: over.id,
        isBelow,
      });

      if (newSchema) {
        form.setData('prompt_json', newSchema);
      }
    }
  }, [form.data.prompt_json, form.setData]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setPointerCoordinates(null);
  }, []);

  // DND: Find active node for DragOverlay
  const activeNode = useMemo(() => activeId ? findNodeById(form.data.prompt_json, activeId) : null, [activeId, form.data.prompt_json]);

  const handleInsertVariable = useCallback((text) => {
    const textarea = promptTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = localPromptText;
    const before = currentText.substring(0, start);
    const after = currentText.substring(end);
    let newText, insertedText, newCursorPos;
    if (text.startsWith('[[') && text.endsWith(']]')) {
      insertedText = text;
      newText = before + insertedText + after;
      newCursorPos = start + insertedText.length;
    } else {
      insertedText = `{{${text}}}`;
      newText = before + insertedText + after;
      newCursorPos = start + insertedText.length;
    }
    setLocalPromptText(newText);
    form.setData('prompt_text', newText);
    setTimeout(() => {
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  }, [localPromptText, form]);

  const handleLongDescriptionChange = useCallback((content) => {
    setLocalLongDescription(content);
    debouncedFormUpdate('long_description', content);
  }, [debouncedFormUpdate]);

  // ADD THIS HANDLER
  const handleAboutAuthorChange = useCallback((content) => {
    setLocalAboutAuthor(content);
    debouncedFormUpdate('about_author', content);
  }, [debouncedFormUpdate]);

  const handleResultDescriptionChange = useCallback((content) => {
    setLocalResultDescription(content);
    debouncedFormUpdate('result_description', content);
  }, [debouncedFormUpdate]);

  const handleVideoOverviewUrlChange = useCallback((e) => {
    const value = e.target.value;
    setLocalVideoOverviewUrl(value);
    debouncedFormUpdate('video_overview_url', value);
  }, [debouncedFormUpdate]);

  const handlePromptUpdate = (updatedPrompt, changeCount, oldVar, newVar) => {
    setLocalPromptText(updatedPrompt);
    form.setData('prompt_text', updatedPrompt);
    if (changeCount > 0) {
      toast.success(`Updated ${changeCount} reference${changeCount > 1 ? 's' : ''}: {{${oldVar}}} → {{${newVar}}}`);
    }
  };

  const scrollToField = (fieldName) => {
    const fieldMap = {
      'title': 'input[name="title"]',
      'description': 'textarea[name="description"]',
      'questions': '.card-header:contains("Questions")',
      'prompt_text': 'textarea[ref="promptTextareaRef"]',
      'prompt_json': '.card-header:contains("Response Schema")'
    };
    const selector = fieldMap[fieldName];
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const showValidationErrors = () => {
    const errorCount = Object.keys(clientErrors).length;
    toast.error(`Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}`);
    const firstErrorField = Object.keys(clientErrors)[0];
    scrollToField(firstErrorField);
  };

  const validateFormData = (data) => {
    const errors = {};
    if (!data.title?.trim()) errors.title = 'Title is required';
    if (!data.description?.trim()) errors.description = 'Description is required';
    // Questions are now optional - removed validation
    // if (!data.questions?.length) errors.questions = 'At least one question is required';
    if (!data.prompt_text?.trim()) {
      errors.prompt_text = 'Prompt text is required';
    } else {
      const variableValidation = validatePromptVariables(data.prompt_text, data.questions);
      if (!variableValidation.isValid) {
        errors.prompt_text = variableValidation.message;
      }
    }
    if (!data.prompt_json?.children?.length) errors.prompt_json = 'Response schema must have at least one field';
    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    console.log('🔵 SAVE FUNCTION CALLED');
    if (isSubmitting) return;
    setIsValidating(true);
    // Merge local state with form data. Note: prompt_json is already synced immediately via handleSchemaChange
    const currentLinkedResources = linkedResourcesRef.current;
    const currentFormData = {
      ...form.data,
      prompt_text: localPromptText,
      long_description: localLongDescription,
      about_author: localAboutAuthor,
      result_description: localResultDescription,
      video_overview_url: localVideoOverviewUrl,
      description: localDescription,
      resources: currentLinkedResources.map((r, i) => ({
        id: r.id,
        sort_order: i,
        context_note: r.context_note || null,
        is_enabled: r.is_enabled,
      })),
    };
    console.log('🔵 Validating form data...', currentFormData);
    const isValid = validateFormData(currentFormData);
    console.log('🔵 Validation result:', isValid);
    if (!isValid) {
      setIsValidating(false);
      showValidationErrors();
      console.log('❌ Validation failed - NOT sending request');
      return;
    }
    console.log('🔵 Validation passed - sending PUT request to:', `/api/formulas-react/${formula.uuid}`);
    setIsSubmitting(true);
    // Use transform to override all data sent in the PUT request
    form.transform((data) => currentFormData);
    form.put(`/api/formulas-react/${formula.uuid}`, {
      preserveScroll: true,
      preserveState: false,
      onSuccess: (page) => {
        // Check if the server returned validation errors despite the redirect
        const serverErrors = page?.props?.errors;
        if (serverErrors && Object.keys(serverErrors).length > 0) {
          // Validation failed — don't show success toast
          Object.entries(serverErrors).forEach(([field, messages]) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            toast.error(`Save failed — ${field}: ${messageArray.join(', ')}`);
          });
          console.error('❌ Save returned validation errors:', serverErrors);
        } else {
          toast.success('Formula saved ✔︎');
          setClientErrors({});
        }
        setIsSubmitting(false);
      },
      onError: (errors) => {
        console.error('❌ Save failed - errors:', errors);

        if (errors && Object.keys(errors).length > 0) {
          Object.entries(errors).forEach(([field, messages]) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            toast.error(`Save failed — ${field}: ${messageArray.join(', ')}`, { duration: 8000 });
          });
        } else {
          toast.error('Save failed — check console for details', { duration: 8000 });
        }

        setIsSubmitting(false);
      },
      onFinish: () => {
        console.log('Request finished');
        setIsValidating(false);
      }
    });
  };

  // Component for Try Formula section with proper React state management
  function TryFormulaSection({ formula, recentScripts }) {
    const [selectedScript, setSelectedScript] = useState('');

    const handleTryFormula = () => {
      if (selectedScript && formula.unique_name) {
        const brainstormUrl = `/formula/use_formula?id=${formula.unique_name}&movieid=${selectedScript}`;
        window.open(brainstormUrl, '_blank');
      }
    };

    return (
      <Row>
        <Col md={8}>
          <Form.Select
            value={selectedScript}
            onChange={(e) => setSelectedScript(e.target.value)}
          >
            <option value="">Select a script to try...</option>

            {recentScripts.my_scripts?.length > 0 && (
              <optgroup label="📝 My Scripts">
                {recentScripts.my_scripts.map(script => (
                  <option key={script.hash} value={script.hash} data-title={script.title}>
                    {script.title}
                  </option>
                ))}
              </optgroup>
            )}

            {recentScripts.script_vault?.length > 0 && (
              <optgroup label="🏛️ Script Vault">
                {recentScripts.script_vault.map(script => (
                  <option key={script.hash} value={script.hash} data-title={script.title}>
                    {script.title}
                  </option>
                ))}
              </optgroup>
            )}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Button
            variant="outline-primary"
            className="w-100"
            disabled={!selectedScript}
            onClick={handleTryFormula}
          >
            <i className="fas fa-external-link-alt me-2"></i>
            Try Formula
          </Button>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Head title="Edit Formula" />
      <div className="container-fluid">
        {hasErrors && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading><i className="fas fa-exclamation-triangle"></i> Errors Found</Alert.Heading>
            {flash?.error && <div className="mb-2"><strong>Server Error:</strong> {flash.error}</div>}
            {(errors || form.errors) && (
              <div>
                <strong>Validation Errors:</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(errors || {}).map(([field, messages]) => (
                    <li key={field}><strong>{field}:</strong> {Array.isArray(messages) ? messages.join(', ') : messages}</li>
                  ))}
                  {Object.entries(form.errors || {}).map(([field, messages]) => (
                    <li key={`form-${field}`}><strong>{field}:</strong> {Array.isArray(messages) ? messages.join(', ') : messages}</li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}
        {flash?.success && <Alert variant="success" className="mb-4"><i className="fas fa-check-circle"></i> {flash.success}</Alert>}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Edit Formula</h1>
        </div>
        {Object.keys(form.errors).length > 0 && (
          <Alert variant="danger" className="mb-4">
            <ul className="mb-0">
              {Object.values(form.errors).flat().map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </Alert>
        )}
        <Tabs
          id="formula-edit-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3 nav-pills"
          fill
          unmountOnExit={false}
          mountOnEnter={true}
        >
          {/* TAB 1: Basic Info */}
          <Tab eventKey="basic" title="1. Basic Info">
            <Card className="mb-4">
              <Card.Header>
                <h4 className="mb-0">📝 Basic Information</h4>
                <small className="text-muted">Formula title, description, and settings</small>
              </Card.Header>
              <Card.Body>
                {isAdmin && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Listed (visible to users)"
                      checked={form.data.listed}
                      onChange={e => form.setData('listed', e.target.checked)}
                    />
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control type="text" value={form.data.title} onChange={e => form.setData('title', e.target.value)} isInvalid={!!clientErrors.title} />
                  {clientErrors.title && <div className="invalid-feedback d-block">{clientErrors.title}</div>}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={localDescription}
                    onChange={e => {
                      setLocalDescription(e.target.value);
                      debouncedFormUpdate('description', e.target.value);
                    }}
                    onBlur={e => immediateFormUpdate('description', e.target.value)}
                    isInvalid={!!clientErrors.description}
                  />
                  {clientErrors.description && <div className="invalid-feedback d-block">{clientErrors.description}</div>}
                </Form.Group>
              </Card.Body>
            </Card>
            <Card className="mb-4">
              <Card.Header>
                <h4 className="mb-0">👤 Author & Explanations</h4>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>About the Author <small className="text-muted">&nbsp;(optional - supports HTML)</small></Form.Label>
                  <LocalTinyMCE
                    key="about_author"
                    value={localAboutAuthor}
                    onChange={handleAboutAuthorChange}
                    onBlur={(content) => immediateFormUpdate('about_author', content)}
                    placeholder="Provide a brief bio or information about the creator of this formula..."
                    height={250}
                  />
                  <Form.Text className="text-muted">
                    This will be displayed on the formula's page to give users context about its creator.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Video Overview URL <small className="text-muted">&nbsp;(optional)</small></Form.Label>
                  <Form.Control
                    type="url"
                    value={localVideoOverviewUrl}
                    onChange={handleVideoOverviewUrlChange}
                    onBlur={(e) => immediateFormUpdate('video_overview_url', e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or Vimeo/Loom URL"
                  />
                  <Form.Text className="text-muted">
                    YouTube, Vimeo, or Loom URL. Displayed as a "Watch overview" link that opens in a modal on the formula page.
                  </Form.Text>
                </Form.Group>

                {/* ── Linked Resources ── */}
                <Form.Group className="mb-3">
                  <Form.Label>Linked Resources <small className="text-muted">&nbsp;(optional)</small></Form.Label>
                  <div style={{ position: 'relative', marginBottom: linkedResources.length ? 12 : 0 }}>
                    <Form.Control
                      type="text"
                      placeholder="Search resources by title..."
                      value={resourceSearch}
                      onChange={e => setResourceSearch(e.target.value)}
                    />
                    {resourceSearch.length >= 2 && (() => {
                      const linkedIds = new Set(linkedResources.map(r => r.id));
                      const filtered = allResources.filter(
                        r => !linkedIds.has(r.id) && r.title.toLowerCase().includes(resourceSearch.toLowerCase())
                      );
                      if (!filtered.length) return null;
                      return (
                        <div style={{
                          position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0,
                          maxHeight: 200, overflowY: 'auto',
                          background: '#fff', border: '1px solid #ced4da', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,.15)'
                        }}>
                          {filtered.slice(0, 20).map(r => (
                            <div
                              key={r.id}
                              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              onClick={() => {
                                setLinkedResources(prev => [...prev, { id: r.id, slug: r.slug, title: r.title, context_note: '', is_enabled: true }]);
                                setResourceSearch('');
                              }}
                            >
                              <small>{r.title}</small>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {linkedResources.length > 0 && (
                    <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                      {linkedResources.map((r, idx) => (
                        <div key={r.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                          borderBottom: idx < linkedResources.length - 1 ? '1px solid #f0f0f0' : 'none',
                          background: r.is_enabled ? '#fff' : '#f8f8f8', opacity: r.is_enabled ? 1 : 0.6
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <button type="button" disabled={idx === 0} onClick={() => {
                              setLinkedResources(prev => { const a = [...prev]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; return a; });
                            }} style={{ border: 'none', background: 'none', padding: 0, cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? .3 : 1, fontSize: 12 }}>▲</button>
                            <button type="button" disabled={idx === linkedResources.length - 1} onClick={() => {
                              setLinkedResources(prev => { const a = [...prev]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return a; });
                            }} style={{ border: 'none', background: 'none', padding: 0, cursor: idx === linkedResources.length - 1 ? 'default' : 'pointer', opacity: idx === linkedResources.length - 1 ? .3 : 1, fontSize: 12 }}>▼</button>
                          </div>
                          <a href={`/guides/${r.slug}`} target="_blank" rel="noreferrer" style={{ whiteSpace: 'nowrap', fontWeight: 500, fontSize: 13, minWidth: 120, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.title}
                          </a>
                          <Form.Control
                            type="text" size="sm" placeholder="Context note (optional)"
                            value={r.context_note || ''}
                            onChange={e => {
                              const val = e.target.value;
                              setLinkedResources(prev => prev.map((x, i) => i === idx ? { ...x, context_note: val } : x));
                            }}
                            style={{ flex: 1, fontSize: 12 }}
                          />
                          <Form.Check
                            type="switch" checked={r.is_enabled}
                            onChange={e => {
                              const val = e.target.checked;
                              setLinkedResources(prev => prev.map((x, i) => i === idx ? { ...x, is_enabled: val } : x));
                            }}
                            title={r.is_enabled ? 'Visible on formula page' : 'Hidden on formula page'}
                            style={{ margin: 0 }}
                          />
                          <button type="button" onClick={() => {
                            setLinkedResources(prev => prev.filter((_, i) => i !== idx));
                          }} style={{ border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }} title="Remove resource">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Link guide resources to this formula. They appear as links on the formula page and this formula appears on each resource's page.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Long Description <small className="text-muted">&nbsp;(optional - supports HTML formatting)</small></Form.Label>
                  <LocalTinyMCE key="long_description" value={localLongDescription} onChange={handleLongDescriptionChange} onBlur={(content) => immediateFormUpdate('long_description', content)} placeholder="Add detailed information, links to resources, examples, etc..." height={300} />
                  <Form.Text className="text-muted">This will be displayed at the top of the formula page. You can include links, formatting, and detailed explanations.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Result Description <small className="text-muted">&nbsp;(optional - supports HTML formatting)</small></Form.Label>
                  <LocalTinyMCE key="result_description" value={localResultDescription} onChange={handleResultDescriptionChange} onBlur={(content) => immediateFormUpdate('result_description', content)} placeholder="Describe what the formula results mean and how to interpret them..." height={250} />
                  <Form.Text className="text-muted">This will be displayed after the formula results to help users understand the output.</Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>

          {/* TAB 2: Question & Prompt */}
          <Tab eventKey="questions" title="2. Question & Prompt">
            <Card className="mb-4">
              <Card.Header>
                <h4 className="mb-0">❓ Questions & Prompt</h4>
                <small className="text-muted">Create questions that generate variables for your prompt</small>
              </Card.Header>
              <Card.Body className="p-0">
                <Row className="g-0">
                  <Col lg={isAdmin && form.data.prompt_mode === 'advanced' ? 8 : 12}>
                    <div className="p-4">
                      <QuestionManager questions={form.data.questions} onQuestionsChange={handleQuestionsChange} promptText={localPromptText} onPromptUpdate={handlePromptUpdate} />
                      {clientErrors.questions && <Alert variant="danger" className="mt-2">{clientErrors.questions}</Alert>}
                      <Card className="mt-4">
                        <Card.Header>
                          <h6 className="mb-0">Prompt Template</h6>
                          <small className="text-muted">Click variables from the sidebar to insert them</small>
                        </Card.Header>
                        <Card.Body>
                          {isAdmin && (
                            <Form.Group className="mb-3 p-3 bg-light rounded border">
                              <Form.Check
                                type="switch"
                                id="prompt-mode-switch"
                                label={form.data.prompt_mode === 'simple' ? "Simple Mode" : "Advanced Mode"}
                                checked={form.data.prompt_mode === 'simple'}
                                onChange={e => form.setData('prompt_mode', e.target.checked ? 'simple' : 'advanced')}
                              />
                              <Form.Text className="text-muted">
                                {form.data.prompt_mode === 'simple'
                                  ? "Simple Mode: Question values are automatically added. Manual {{variables}} are ignored."
                                  : "Advanced Mode: You must manually insert {{variables}} and [[functions]]."
                                }
                              </Form.Text>
                            </Form.Group>
                          )}
                          <Form.Group className="mb-3">
                            <Form.Control ref={promptTextareaRef} as="textarea" rows={8} placeholder="Write your prompt here. Use variables like {{feeling}} and {{title}}..." value={localPromptText} onChange={e => { setLocalPromptText(e.target.value); debouncedFormUpdate('prompt_text', e.target.value); }} onBlur={e => { immediateFormUpdate('prompt_text', e.target.value); }} isInvalid={!!clientErrors.prompt_text} className={`font-monospace ${clientErrors.prompt_text ? 'is-invalid' : ''}`} />
                            {clientErrors.prompt_text && <div className="invalid-feedback d-block">{clientErrors.prompt_text}</div>}
                            <Form.Text className="text-muted">💡 Variables will be replaced with user input when the formula is executed</Form.Text>
                          </Form.Group>
                          {(isAdmin && form.data.prompt_mode === 'advanced') && (
                            <Card className="bg-light border-0">
                              <Card.Header className="bg-transparent border-0 pb-2">
                                <h6 className="mb-0 text-secondary">📄 Context Options</h6>
                                <small className="text-muted">Choose what background information to include with the prompt</small>
                              </Card.Header>
                              <Card.Body className="pt-0">
                                <Row>
                                  <Col md={8}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="fw-medium">Include Script Context</Form.Label>
                                      <Form.Select value={form.data.context_inclusion} onChange={e => form.setData('context_inclusion', e.target.value)}>
                                        <option value="synopsis">Include Synopsis Only</option>
                                        <option value="scene_summary">Include Scene-by-Scene Summary</option>
                                        <option value="full_text">Include Full Script Text</option>
                                      </Form.Select>
                                      <Form.Text className="text-muted">Controls how much script information is provided to the AI</Form.Text>
                                    </Form.Group>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Group className="mb-3">
                                      <Form.Label className="fw-medium">Additional Context</Form.Label>
                                      <div className="mt-2">
                                        <Form.Check type="checkbox" label="Include Character Descriptions" checked={form.data.include_character_descriptions} onChange={e => form.setData('include_character_descriptions', e.target.checked)} />
                                        <Form.Text className="text-muted">Adds character profiles and arcs</Form.Text>
                                      </div>
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>
                  {isAdmin && form.data.prompt_mode === 'advanced' ? (
                    <Col lg={4} className="border-start bg-light">
                      <div className="sticky-top p-3" style={{ top: '20px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                        <VariablesSidebar questions={form.data.questions || []} onInsertVariable={handleInsertVariable} />
                        {process.env.NODE_ENV === 'development' && (
                          <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                            <small className="text-info">🐛 Debug: {form.data.questions?.length || 0} questions available</small>
                          </div>
                        )}
                      </div>
                    </Col>
                  ) : (
                    <Col lg={4} className="border-start bg-light">
                      <div className="sticky-top p-3" style={{ top: '20px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                        <SimpleModeSidebar
                          contextSettings={{
                            context_inclusion: form.data.context_inclusion,
                            include_character_descriptions: form.data.include_character_descriptions
                          }}
                          onContextChange={handleContextChange}
                        />
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* TAB 3: Response Schema */}
          <Tab eventKey="schema" title="3. Response Schema">
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">🔧 Response Schema</h4>
                  <small className="text-muted">Define the structure for the AI response</small>
                </div>
                <div className="btn-group">
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowSchemaImport(true)}>📥 Import</Button>
                  <Button variant="outline-info" size="sm" onClick={handleSchemaExport}>📤 Export</Button>
                </div>
              </Card.Header>
              <Card.Body>
                {clientErrors.prompt_json && <Alert variant="danger" className="m-4">{clientErrors.prompt_json}</Alert>}

                {/* DND: The DndContext is the ONLY provider at this level. */}
                <DndContext
                  sensors={sensors}
                  // DND: Use the enhanced collision strategy with edge detection
                  collisionDetection={collisionDetectionStrategy}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <div className="bg-light border-bottom">
                    <div className="p-4">
                      <WYSIWYGCanvas
                        schema={form.data.prompt_json}
                        onSchemaChange={handleSchemaChange}
                        activeId={activeId} // Pass activeId to hide the original item
                        pointerCoordinates={pointerCoordinates} // Pass pointer coordinates for precise positioning
                      />
                    </div>
                  </div>

                  {/* DND: Use createPortal to render the DragOverlay at the root of the document.
                      This isolates it from parent transforms and positioning contexts, fixing the visual glitches. */}
                  {createPortal(
                    <DragOverlay
                      adjustScale={false}
                      dropAnimation={{
                        duration: 250,
                        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                      }}
                    >
                      {activeNode ? (
                        <div className="bg-white shadow-lg rounded p-2">
                          <WYSIWYGCanvas.StaticRender node={activeNode} />
                        </div>
                      ) : null}
                    </DragOverlay>,
                    document.body
                  )}

                  <details className="p-4">
                    <summary><h6 className="d-inline-block mb-0 cursor-pointer">Technical Schema Builder <small className="text-muted ms-2">(Tree Editor)</small></h6></summary>
                    <div className="mt-3">
                      <SchemaBuilder value={form.data.prompt_json} variables={variables} onSchemaChange={handleSchemaChange} />
                    </div>
                  </details>
                </DndContext>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        <SchemaImportModal show={showSchemaImport} onHide={() => setShowSchemaImport(false)} onImport={handleSchemaImport} />
        <div className="d-flex justify-content-between align-items-center mb-5 mt-4 p-3 border-top sticky-bottom bg-light">
          {/* Left Side: Compact "Try Formula" */}
          <CompactTryFormula formula={formula} recentScripts={recentScripts} />

          {/* Right Side: Save Button */}
          <Button
            variant="success"
            size="lg"
            onClick={save}
            disabled={isSubmitting || Object.keys(clientErrors).length > 0}
            className="d-flex align-items-center"
          >
            {isSubmitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
            ) : Object.keys(clientErrors).length > 0 ? (
              <>❌ Fix Errors First ({Object.keys(clientErrors).length})</>
            ) : (
              <>💾 Save Formula</>
            )}
          </Button>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </>
  );
}
